import { useCallback, useRef, useState } from 'react'
import { useSWRConfig } from 'swr'
import { message } from 'antd'
import { useAppStore } from '@forwardflow/store/appStore'
import { campaignV2Api } from '../services'
import {
   ICatalogItem,
   CampaignV2Error,
   ICampaignConfiguration,
} from '../interfaces/campaign-v2.types'
import { idempotencyKey, syncPools } from '../utils/config'
import { hasContentEdits, useCampaignV2Store } from '../store/editor.store'

/**
 * Save, publish, activate, pause: the lifecycle calls in one hook so every
 * step saves the same way. Handoff rules kept here:
 *  - PATCH sends the full configuration with expectedRevision (section 6)
 *  - 409 reloads and keeps the user's copy for comparison (section 15)
 *  - one Idempotency-Key per deliberate operation, reused on retry (section 3)
 */
export const useCampaignV2Editor = () => {
   const store = useCampaignV2Store()
   const { mutate } = useSWRConfig()
   const contextGuard = () => {
      const org = useAppStore.getState().currentOrganization
      const id = useCampaignV2Store.getState().campaignId
      return () => {
         if (
            org !== useAppStore.getState().currentOrganization ||
            id !== useCampaignV2Store.getState().campaignId
         )
            throw Error(
               'The organization or campaign changed. Open the campaign again to continue.'
            )
      }
   }
   const [saving, setSaving] = useState(false)
   const [activating, setActivating] = useState(false)
   const [error, setError] = useState<string | null>(null)
   // Keys survive a retry of the same operation, and are dropped on success.
   const keys = useRef<Record<string, string>>({})
   const keyFor = (scope: string) => {
      if (!keys.current[scope]) keys.current[scope] = idempotencyKey(scope)
      return keys.current[scope]
   }
   const clearKey = (scope: string) => {
      delete keys.current[scope]
   }

   const invalidate = useCallback(
      (campaignId: string) => {
         void mutate(
            (k) =>
               Array.isArray(k) &&
               k[0] === 'CAMPAIGN_V2' &&
               (k[1] === 'live' || k[1] === 'preview') &&
               k[2] === campaignId &&
               k[k.length - 1] === useAppStore.getState().currentOrganization
         )
      },
      [mutate]
   )

   /** The saved configuration is already canonical. Preserve every untouched field. */
   const outgoing = (config: ICampaignConfiguration): ICampaignConfiguration =>
      config

   const describe = (e: unknown) => {
      if (e instanceof CampaignV2Error) {
         if (e.code === 'campaign_phone_number_required')
            return 'Pick a campaign number before activating.'
         if (e.code === 'routing_agent_not_in_organization')
            return 'One of the chosen agents is not in this organization.'
         if (e.code === 'phone_purchase_requires_reconciliation')
            return 'The number purchase needs confirmation before anything else is bought. Nothing was charged twice.'
         if (e.status === 401 || e.status === 403)
            return 'You do not have permission for that, or your session expired.'
         return e.message
      }
      return e instanceof Error ? e.message : 'Something went wrong. Try again.'
   }

   const load = useCallback(
      async (campaignId: string) => {
         const guard = contextGuard()
         const api = campaignV2Api()
         const read = await api.readConfiguration(campaignId)
         guard()
         store.applyRead(read)
         return read
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
   )

   /**
    * Create the draft on first save, PATCH afterwards. Returns the id so a
    * brand-new campaign can move to its own URL.
    */
   const save = useCallback(async (): Promise<string | null> => {
      const s = useCampaignV2Store.getState()
      if (!s.config) return null
      if (hasContentEdits(s.contentDrafts)) {
         setError('Save or cancel your content edits first.')
         return null
      }
      const guard = contextGuard()
      const api = campaignV2Api()
      setSaving(true)
      setError(null)
      /* A draft saves whatever its state: unset choices fall back to the
         program defaults instead of blocking the save — seller/buyer
         categories, the category's General content in each empty pool,
         a described subtype per category on mixed campaigns, and jump-ball
         routing across every agent. */
      const withDraftDefaults = async (config: ICampaignConfiguration) => {
         let next = config
         if (!next.businessTypeIds?.length) {
            const cats = await api.businessCategories().catch(() => [])
            const pick = (re: RegExp) =>
               cats.find((c) => re.test(`${c.slug || ''} ${c.name}`))?.id
            const ids = [pick(/sell|listing/i), pick(/buy/i)].filter(
               (v): v is number => v != null
            )
            const fallback = ids.length ? ids : cats.slice(0, 1).map((c) => c.id)
            if (fallback.length)
               next = syncPools(
                  next,
                  fallback,
                  Object.fromEntries(cats.map((c) => [c.id, c.name]))
               )
         }
         /* The backend refuses a pool with no outbound script once the cadence
            has calls: empty pools inherit the category's General catalog
            content (acquired so the pool gets a {id, revisionId} pin). */
         const inherit = async (pool: (typeof next.contentPools)[number]) => {
            const needs =
               !pool.outboundScripts?.length ||
               !pool.inboundScripts?.length ||
               !pool.conversationContent?.length ||
               !pool.strategies?.length
            if (!needs) return pool
            try {
               const acquire = async (
                  kind: Parameters<typeof api.catalog>[0],
                  pick: (items: ICatalogItem[]) => ICatalogItem | undefined
               ) => {
                  const items = await api.catalog(kind, pool.businessTypeId)
                  const item = pick(items)
                  if (!item) return null
                  const rev = await api.acquireRevision(kind, item.contentId)
                  return { id: String(item.contentId), revisionId: rev.id }
               }
               /* The program defaults are the Gemini "General" pieces:
                  Buyer-General-Gemini / Seller-GeneralInquiry-Gemini and their
                  inbound + outbound content-group counterparts. */
               const byPreference = (
                  items: ICatalogItem[],
                  prefers: RegExp[]
               ) => {
                  for (const re of prefers) {
                     const hit = items.find((i) => re.test(i.name))
                     if (hit) return hit
                  }
                  return items[0]
               }
               const [phone, inbound, strategy, conversation] =
                  await Promise.all([
                     acquire('phone', (items) =>
                        byPreference(
                           items.filter((i) => !/inbound/i.test(i.name)),
                           [/general.*gemini/i, /gemini/i, /general/i]
                        )
                     ),
                     acquire('phone', (items) =>
                        byPreference(
                           items.filter((i) => /inbound/i.test(i.name)),
                           [/general.*gemini.*reactivate/i, /general.*gemini/i, /gemini/i, /general/i]
                        )
                     ),
                     acquire('strategy', (items) =>
                        byPreference(items, [
                           /general.*gemini/i,
                           /general/i,
                           /gemini/i,
                        ])
                     ),
                     acquire('conversation', (items) =>
                        byPreference(items, [/general/i, /gemini/i])
                     ),
                  ])
               if (!phone && !pool.outboundScripts?.length) return pool
               return {
                  ...pool,
                  outboundScripts: pool.outboundScripts?.length
                     ? pool.outboundScripts
                     : phone
                       ? [phone]
                       : [],
                  inboundScripts: pool.inboundScripts?.length
                     ? pool.inboundScripts
                     : inbound
                       ? [inbound]
                       : [],
                  strategies: pool.strategies?.length
                     ? pool.strategies
                     : strategy
                       ? [strategy]
                       : [],
                  conversationContent: pool.conversationContent?.length
                     ? pool.conversationContent
                     : conversation
                       ? [conversation]
                       : [],
               }
            } catch {
               return pool
            }
         }
         next = {
            ...next,
            contentPools: await Promise.all(next.contentPools.map(inherit)),
         }
         /* A mixed campaign needs a way to tell its categories apart: default
            to one described subtype per category, routing to that category's
            pool. */
         if (
            next.businessTypeIds.length > 1 &&
            !next.subtypes?.length &&
            !next.businessTypeRules?.length
         ) {
            const label = (id: number) =>
               ({ 1: 'Seller', 4: 'Buyer', 5: 'Renter' })[id] ?? `Type ${id}`
            const blurb: Record<string, string> = {
               Seller:
                  'The lead is interested in selling a property: valuation requests, listing interest, cash offers, or any selling signal.',
               Buyer:
                  'The lead is interested in buying a property: searching listings, financing questions, showings, or any buying signal.',
               Renter:
                  'The lead is interested in renting: rental listings, lease questions, or tenancy signals.',
            }
            next = {
               ...next,
               subtypes: next.businessTypeIds.map((id, i) => {
                  const name = label(id)
                  const pool =
                     next.contentPools.find((p) => p.businessTypeId === id)
                        ?.id ?? next.contentPools[0]?.id
                  return {
                     id: `${name.toLowerCase()}-general-type`,
                     name,
                     poolId: pool,
                     priority: i + 1,
                     conditions: [],
                     description:
                        blurb[name] ?? `General ${name.toLowerCase()} leads.`,
                  }
               }),
            }
         }
         if (!next.routing?.agentIds?.length) {
            const agents = await api.agents().catch(() => [])
            if (agents.length)
               next = {
                  ...next,
                  routing: {
                     enabled: true,
                     strategy: 'jump_ball',
                     agentIds: agents.map((a) => a.crmUserId),
                  },
               }
         }
         /* Readiness refuses a config with no incomingCallPoolId: unclassified
            inbound callers need a pool. Default to the default pool. */
         if (
            next.contentPools.length &&
            !next.contentPools.some((p) => p.id === next.incomingCallPoolId)
         ) {
            const pool =
               next.contentPools.find((p) => p.isDefault) ??
               next.contentPools[0]
            next = { ...next, incomingCallPoolId: pool.id }
         }
         return next
      }
      try {
         if (!s.campaignId) {
            const read = await api.createDraft(
               s.name || 'New campaign',
               await withDraftDefaults(outgoing(s.config)),
               keyFor('draft')
            )
            clearKey('draft')
            guard()
            store.applyRead(read)
            /* The default voice: a real-person persona named Erin. Replaced the
               moment the user picks their own on the voice step. */
            api.addAssistant(read.campaignId, {
               assistantName: 'Erin',
               voiceId: 'Sulafat',
            }).catch(() => undefined)
            return read.campaignId
         }
         const read = await api.saveConfiguration(
            s.campaignId,
            s.revision,
            await withDraftDefaults(outgoing(s.config)),
            s.name
         )
         guard()
         store.applyRead(read)
         invalidate(s.campaignId)
         return read.campaignId
      } catch (e) {
         if (e instanceof CampaignV2Error && e.status === 409 && s.campaignId) {
            const fresh = await api.readConfiguration(s.campaignId)
            store.setConflict(fresh)
            setError(
               'This campaign was changed somewhere else. Review the latest version before saving again.'
            )
            return null
         }
         const text = describe(e)
         setError(text)
         message.error(text)
         return null
      } finally {
         setSaving(false)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [invalidate])

   /** Take the server's version and drop local edits (user's choice on conflict). */
   const acceptConflict = useCallback(() => {
      const s = useCampaignV2Store.getState()
      if (s.conflict) store.applyRead(s.conflict)
      setError(null)
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   /** Publish the current draft only (no activation, no purchase). */
   const publish = useCallback(async () => {
      const s = useCampaignV2Store.getState()
      if (!s.campaignId) return false
      if (hasContentEdits(s.contentDrafts)) {
         setError('Save or cancel your content edits first.')
         return false
      }
      const guard = contextGuard()
      const api = campaignV2Api()
      setSaving(true)
      try {
         if (s.dirty) {
            const read = await api.saveConfiguration(
               s.campaignId,
               s.revision,
               outgoing(s.config as ICampaignConfiguration),
               s.name
            )
            guard()
            store.applyRead(read)
         }
         guard()
         const current = useCampaignV2Store.getState()
         await api.publish(
            s.campaignId,
            current.revision,
            keyFor(`publish-${current.revision}`)
         )
         clearKey(`publish-${current.revision}`)
         await load(s.campaignId)
         invalidate(s.campaignId)
         message.success('Changes published')
         return true
      } catch (e) {
         const text = describe(e)
         setError(text)
         message.error(text)
         return false
      } finally {
         setSaving(false)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [invalidate, load])

   /**
    * The convenient route: publishes the current draft and activates it,
    * buying any reserved numbers. Unsaved edits are saved first so the
    * user never activates something they have not seen.
    */
   const activate = useCallback(async () => {
      const s = useCampaignV2Store.getState()
      if (!s.campaignId || !s.config) return false
      if (hasContentEdits(s.contentDrafts)) {
         setError('Save or cancel your content edits first.')
         return false
      }
      const guard = contextGuard()
      const api = campaignV2Api()
      setActivating(true)
      setError(null)
      try {
         if (s.dirty) {
            const read = await api.saveConfiguration(
               s.campaignId,
               s.revision,
               outgoing(s.config),
               s.name
            )
            guard()
            store.applyRead(read)
         }
         guard()
         await api.activate(
            s.campaignId,
            keyFor(
               `activate-${s.campaignId}-${useCampaignV2Store.getState().revision}`
            )
         )
         clearKey(
            `activate-${s.campaignId}-${useCampaignV2Store.getState().revision}`
         )
         await load(s.campaignId)
         invalidate(s.campaignId)
         return true
      } catch (e) {
         const text = describe(e)
         setError(text)
         message.error(text)
         if (e instanceof CampaignV2Error && e.status === 409) {
            const fresh = await api.readConfiguration(s.campaignId)
            store.setConflict(fresh)
         }
         return false
      } finally {
         setActivating(false)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [invalidate, load])

   const pause = useCallback(async () => {
      const s = useCampaignV2Store.getState()
      if (!s.campaignId) return
      try {
         await campaignV2Api().setStatus(s.campaignId, 'paused')
         await load(s.campaignId)
         invalidate(s.campaignId)
         message.success(
            'Campaign paused. Nothing new goes out until you resume.'
         )
      } catch (e) {
         message.error(describe(e))
      }
   }, [invalidate, load])

   /** Resuming is a deliberate activation with a fresh key (section 11). */
   const resume = useCallback(async () => {
      const s = useCampaignV2Store.getState()
      if (!s.campaignId) return
      try {
         await campaignV2Api().activate(
            s.campaignId,
            idempotencyKey(`resume-${s.campaignId}`)
         )
         await load(s.campaignId)
         invalidate(s.campaignId)
         message.success('Campaign resumed')
      } catch (e) {
         message.error(describe(e))
      }
   }, [invalidate, load])

   return {
      load,
      save,
      publish,
      activate,
      pause,
      resume,
      acceptConflict,
      saving,
      activating,
      error,
      setError,
      describe,
   }
}
